import { formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, ChevronRight, ChevronUp, FileText } from 'lucide-react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/utils';
import { useGetArtifactUnpackedQuery } from '@/services/api/artifactService';
import { useGetTaskQuery } from '@/services/api/tasksService';
import type {
  WikiCitation,
  WikiPage,
} from '@/services/types/matter-wiki-types';

import {
  extractCitationTargetId,
  getCitationDisplayLabel,
  getCitationKey,
  normalizeCitationSourceType,
  transformWikiContentWithFootnotes,
  type WikiCitationFootnote,
} from './utils/citations';

interface ChildPageLink {
  path: string;
  title: string;
}

export interface WikiScrollViewHandle {
  scrollToPath: (path: string) => void;
}

interface WikiScrollViewProps {
  matterId: string;
  pages: WikiPage[];
  onActivePathChange: (path: string) => void;
  onCitationSelect: (citation: WikiCitation) => void;
  activeCitationKey: string | null;
}

const slideVariants = {
  enter: (dir: number) => ({
    y: dir > 0 ? '100%' : '-100%',
    zIndex: 2,
  }),
  center: {
    y: '0%',
    zIndex: 1,
  },
  exit: () => ({
    zIndex: 0,
    scale: 0.96,
    opacity: 0.3,
  }),
};

const springTransition = {
  y: { type: 'spring' as const, stiffness: 320, damping: 32, mass: 0.8 },
  opacity: { duration: 0.25, ease: [0.32, 0.72, 0, 1] },
  scale: { duration: 0.35, ease: [0.32, 0.72, 0, 1] },
};

const OVERSCROLL_THRESHOLD = 60;

const PROSE_CLASSES = [
  'prose prose-zinc prose-sm max-w-none',
  'prose-headings:font-semibold prose-headings:tracking-tight',
  'prose-h1:text-xl prose-h1:border-b prose-h1:border-zinc-100 prose-h1:pb-2 prose-h1:mb-4',
  'prose-h2:text-lg prose-h2:mt-8 prose-h2:mb-3',
  'prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2',
  'prose-p:text-zinc-600 prose-p:leading-relaxed',
  'prose-li:text-zinc-600',
  'prose-strong:text-zinc-800 prose-strong:font-semibold',
  'prose-a:text-zinc-900 prose-a:underline prose-a:decoration-zinc-300 hover:prose-a:decoration-zinc-500',
  'prose-code:text-zinc-700 prose-code:bg-zinc-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-normal prose-code:before:content-none prose-code:after:content-none',
  'prose-pre:bg-zinc-50 prose-pre:border prose-pre:border-zinc-200 prose-pre:rounded-lg',
  'prose-table:text-sm',
  'prose-th:text-zinc-500 prose-th:font-medium prose-th:text-left prose-th:pb-2 prose-th:border-b prose-th:border-zinc-200',
  'prose-td:py-2 prose-td:border-b prose-td:border-zinc-50',
  'prose-hr:border-zinc-100',
].join(' ');

const MarkdownLink: React.FC<React.ComponentProps<'a'>> = ({
  href,
  children,
  ...props
}) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
    {children}
  </a>
);

const MarkdownTable: React.FC<React.ComponentProps<'table'>> = ({
  children,
  ...props
}) => (
  <div className="overflow-x-auto rounded-lg border border-zinc-100">
    <table {...props}>{children}</table>
  </div>
);

const markdownComponents: Components = {
  a: MarkdownLink,
  table: MarkdownTable,
};

function createCiteAwareComponents(
  footnotesByNumber: Map<number, WikiCitationFootnote>,
  onCitationSelect: (citation: WikiCitation) => void,
): Components {
  return {
    ...markdownComponents,
    a: ({ href, children, ...props }) => {
      if (href?.startsWith('#wiki-cite-')) {
        const number = Number(href.replace('#wiki-cite-', ''));
        const footnote = footnotesByNumber.get(number);

        return (
          <button
            type="button"
            onClick={() => {
              if (footnote) {
                onCitationSelect(footnote.citation);
              }
            }}
            className="mx-0.5 inline-flex items-center rounded-sm border border-zinc-300 px-1 text-[11px] font-medium text-zinc-700 hover:border-zinc-400 hover:text-zinc-900"
          >
            {children}
          </button>
        );
      }

      return (
        <MarkdownLink href={href} {...props}>
          {children}
        </MarkdownLink>
      );
    },
  };
}

const ArtifactFootnoteName: React.FC<{ matterId: string; artifactId: string }> = ({
  matterId,
  artifactId,
}) => {
  const { data } = useGetArtifactUnpackedQuery(
    {
      matterId,
      artifactId,
    },
    {
      skip: !artifactId,
    },
  );

  const displayName =
    data?.blob?.name ||
    data?.artifact_digest?.title ||
    `Artifact ${artifactId.slice(0, 8)}`;

  return <span>{displayName}</span>;
};

const TaskFootnoteName: React.FC<{ matterId: string; taskId: string }> = ({
  matterId,
  taskId,
}) => {
  const { data } = useGetTaskQuery(
    { matterId, taskId },
    { skip: !taskId },
  );

  const displayName = data?.name || `Task ${taskId.slice(0, 8)}`;

  return <span>{displayName}</span>;
};

const CitationFootnoteItem: React.FC<{
  matterId: string;
  footnote: WikiCitationFootnote;
  isActive: boolean;
  onCitationSelect: (citation: WikiCitation) => void;
}> = ({ matterId, footnote, isActive, onCitationSelect }) => {
  const sourceType = normalizeCitationSourceType(footnote.citation.source_type);
  const sourceId = extractCitationTargetId(String(footnote.citation.source_id));

  let nameNode: React.ReactNode;
  if (sourceType === 'artifact') {
    nameNode = <ArtifactFootnoteName matterId={matterId} artifactId={sourceId} />;
  } else if (sourceType === 'task') {
    nameNode = <TaskFootnoteName matterId={matterId} taskId={sourceId} />;
  } else {
    nameNode = <span>{getCitationDisplayLabel(footnote.citation)}</span>;
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onCitationSelect(footnote.citation)}
        className={cn(
          'w-full rounded-md px-2 py-1.5 text-left text-xs transition-colors',
          isActive
            ? 'bg-blue-50 text-blue-700'
            : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900',
        )}
      >
        <span className="font-medium text-zinc-500">[{footnote.number}] </span>
        {nameNode}
      </button>
    </li>
  );
};

const WikiFootnotes: React.FC<{
  matterId: string;
  footnotes: WikiCitationFootnote[];
  onCitationSelect: (citation: WikiCitation) => void;
  activeCitationKey: string | null;
}> = ({ matterId, footnotes, onCitationSelect, activeCitationKey }) => {
  if (footnotes.length === 0) {
    return null;
  }

  return (
    <section className="mt-8 border-t border-zinc-100 pt-5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
        Sources
      </p>
      <ol className="mt-2 space-y-1">
        {footnotes.map((footnote) => (
          <CitationFootnoteItem
            key={footnote.number}
            matterId={matterId}
            footnote={footnote}
            isActive={activeCitationKey === getCitationKey(footnote.citation)}
            onCitationSelect={onCitationSelect}
          />
        ))}
      </ol>
    </section>
  );
};

const PageContent: React.FC<{
  matterId: string;
  page: WikiPage;
  childPages: ChildPageLink[];
  onNavigateToChild: (path: string) => void;
  onCitationSelect: (citation: WikiCitation) => void;
  activeCitationKey: string | null;
}> = ({
  matterId,
  page,
  childPages,
  onNavigateToChild,
  onCitationSelect,
  activeCitationKey,
}) => {
  const updatedAgo = formatDistanceToNow(new Date(page.modified_at), {
    addSuffix: true,
  });

  const hasContent = page.content.trim().length > 0;

  const transformed = useMemo(
    () => transformWikiContentWithFootnotes(page.content, page.citations ?? []),
    [page.content, page.citations],
  );

  const footnotesByNumber = useMemo(
    () => new Map(transformed.footnotes.map((footnote) => [footnote.number, footnote])),
    [transformed.footnotes],
  );

  const pageMarkdownComponents = useMemo<Components>(
    () => createCiteAwareComponents(footnotesByNumber, onCitationSelect),
    [footnotesByNumber, onCitationSelect],
  );

  return (
    <>
      <header className="border-b border-zinc-100 pb-4 mb-5">
        <h1 className="text-xl font-semibold text-zinc-900 tracking-tight">
          {page.title}
        </h1>
        <p className="text-[11px] text-zinc-400 mt-1.5">{updatedAgo}</p>
      </header>

      {hasContent && (
        <>
          <div className={PROSE_CLASSES}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              components={pageMarkdownComponents}
            >
              {transformed.content}
            </ReactMarkdown>
          </div>

          <WikiFootnotes
            matterId={matterId}
            footnotes={transformed.footnotes}
            onCitationSelect={onCitationSelect}
            activeCitationKey={activeCitationKey}
          />
        </>
      )}

      {childPages.length > 0 && (
        <div className={cn(hasContent && 'mt-8 pt-6 border-t border-zinc-100')}>
          {!hasContent && (
            <p className="text-sm text-zinc-400 mb-4">
              {childPages.length} {childPages.length === 1 ? 'page' : 'pages'}{' '}
              in this section
            </p>
          )}
          <div className="space-y-1">
            {childPages.map((child) => (
              <button
                key={child.path}
                type="button"
                onClick={() => onNavigateToChild(child.path)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5',
                  'text-left text-sm text-zinc-600',
                  'transition-all duration-150',
                  'hover:bg-zinc-50 hover:text-zinc-900',
                  'cursor-pointer group',
                )}
              >
                <FileText className="h-4 w-4 text-zinc-300 shrink-0 group-hover:text-zinc-400 transition-colors" />
                <span className="truncate flex-1">{child.title}</span>
                <ChevronRight className="h-3.5 w-3.5 text-zinc-200 shrink-0 group-hover:text-zinc-400 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export const WikiScrollView = forwardRef<
  WikiScrollViewHandle,
  WikiScrollViewProps
>(({ matterId, pages, onActivePathChange, onCitationSelect, activeCitationKey }, ref) => {
  const [[currentIndex, direction], setPage] = useState<[number, number]>([
    0, 0,
  ]);
  const contentElRef = useRef<HTMLDivElement | null>(null);
  const isAnimatingRef = useRef(false);
  const cooldownRef = useRef(false);

  const navigateTo = useCallback(
    (idx: number, dir?: number) => {
      if (isAnimatingRef.current) return;
      if (idx < 0 || idx >= pages.length) return;
      if (idx === currentIndex) return;

      const resolvedDir = dir ?? (idx > currentIndex ? 1 : -1);
      isAnimatingRef.current = true;
      setPage([idx, resolvedDir]);
      onActivePathChange(pages[idx].path);
    },
    [currentIndex, pages, onActivePathChange],
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToPath: (path: string) => {
        const idx = pages.findIndex((p) => p.path === path);
        if (idx !== -1) navigateTo(idx);
      },
    }),
    [pages, navigateTo],
  );

  useEffect(() => {
    const el = contentElRef.current;
    if (!el) return undefined;

    let accum = 0;

    const handleWheel = (e: WheelEvent) => {
      if (isAnimatingRef.current || cooldownRef.current) return;

      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 2;
      const atTop = el.scrollTop < 2;
      const fitsInView = el.scrollHeight <= el.clientHeight + 2;

      if ((atBottom || fitsInView) && e.deltaY > 0) {
        accum += e.deltaY;
        e.preventDefault();
        if (accum > OVERSCROLL_THRESHOLD) {
          accum = 0;
          navigateTo(currentIndex + 1, 1);
        }
      } else if ((atTop || fitsInView) && e.deltaY < 0) {
        accum += Math.abs(e.deltaY);
        e.preventDefault();
        if (accum > OVERSCROLL_THRESHOLD) {
          accum = 0;
          navigateTo(currentIndex - 1, -1);
        }
      } else {
        accum = 0;
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [currentIndex, navigateTo]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isAnimatingRef.current) return;

      if (e.key === 'PageDown') {
        e.preventDefault();
        navigateTo(currentIndex + 1, 1);
      } else if (e.key === 'PageUp') {
        e.preventDefault();
        navigateTo(currentIndex - 1, -1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        navigateTo(currentIndex + 1, 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        navigateTo(currentIndex - 1, -1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, navigateTo]);

  const contentRefCallback = useCallback((el: HTMLDivElement | null) => {
    contentElRef.current = el;
    if (el) el.scrollTop = 0;
  }, []);

  const handleExitComplete = useCallback(() => {
    isAnimatingRef.current = false;
    cooldownRef.current = true;
    setTimeout(() => {
      cooldownRef.current = false;
    }, 300);
  }, []);

  const navigateToPath = useCallback(
    (path: string) => {
      const idx = pages.findIndex((p) => p.path === path);
      if (idx !== -1) navigateTo(idx);
    },
    [pages, navigateTo],
  );

  const currentPage = pages[currentIndex];
  if (!currentPage) return null;

  const childPages: ChildPageLink[] = pages
    .filter((p) => {
      if (!p.path.startsWith(`${currentPage.path}/`)) return false;
      const remainder = p.path.slice(currentPage.path.length + 1);
      return remainder.length > 0 && !remainder.includes('/');
    })
    .map((p) => ({ path: p.path, title: p.title }));

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < pages.length - 1;

  return (
    <div className="h-full relative overflow-hidden bg-white">
      <AnimatePresence
        initial={false}
        custom={direction}
        mode="popLayout"
        onExitComplete={handleExitComplete}
      >
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={springTransition}
          className="absolute inset-0 bg-white"
        >
          <div
            ref={contentRefCallback}
            className={cn(
              'h-full overflow-y-auto overscroll-none',
              '[&::-webkit-scrollbar]:w-1.5',
              '[&::-webkit-scrollbar-track]:bg-transparent',
              '[&::-webkit-scrollbar-thumb]:bg-zinc-200 [&::-webkit-scrollbar-thumb]:rounded-full',
              'hover:[&::-webkit-scrollbar-thumb]:bg-zinc-300',
            )}
          >
            <div className="max-w-3xl mx-auto px-8 py-8 pb-24">
              <PageContent
                matterId={matterId}
                page={currentPage}
                childPages={childPages}
                onNavigateToChild={navigateToPath}
                onCitationSelect={onCitationSelect}
                activeCitationKey={activeCitationKey}
              />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {hasPrev && (
        <button
          type="button"
          onClick={() => navigateTo(currentIndex - 1, -1)}
          className={cn(
            'absolute top-0 inset-x-0 h-10 z-10',
            'flex items-center justify-center',
            'opacity-0 hover:opacity-100 transition-opacity duration-200',
            'cursor-pointer',
          )}
          aria-label="Previous page"
        >
          <div className="flex items-center gap-1 text-[10px] text-zinc-300">
            <ChevronUp className="h-3 w-3" />
            <span>{pages[currentIndex - 1].title}</span>
          </div>
        </button>
      )}

      {hasNext && (
        <button
          type="button"
          onClick={() => navigateTo(currentIndex + 1, 1)}
          className={cn(
            'absolute bottom-0 inset-x-0 h-10 z-10',
            'flex items-center justify-center',
            'opacity-0 hover:opacity-100 transition-opacity duration-200',
            'cursor-pointer',
          )}
          aria-label="Next page"
        >
          <div className="flex items-center gap-1 text-[10px] text-zinc-300">
            <ChevronDown className="h-3 w-3" />
            <span>{pages[currentIndex + 1].title}</span>
          </div>
        </button>
      )}

      <div className="absolute bottom-3 right-5 text-[10px] text-zinc-300 font-medium tabular-nums pointer-events-none select-none">
        {currentIndex + 1} / {pages.length}
      </div>
    </div>
  );
});

WikiScrollView.displayName = 'WikiScrollView';
